import { forwardRef } from 'react';

import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './Box.scss';

/**
 * The Box component is a "layout primitive."
 * It is responsible for the following behaviors:
 * - Wrapping elements into a block display context
 * - Providing padding
 * - Giving the same margin of 0 for all first-level children.
 * - Specifying sane defaults for rendering as a child of a flex context
 *
 * When would I use a Box over a raw div?
 * - When I want the above behaviors
 * - When I want to composably style a div with simple layout
 *
 * When would I not use a Box over a raw div?
 * - When I want to do more complex styling that does not fit into the above buckets
 *
 * Example usage:
 * ```
 * // MyComponent.js
 * <Box className={styles['my-component']}>
 *   <CoolComponent />
 * </Box>
 *
 * // MyComponent.scss
 * .my-component {
 *   composes: box--padding__public from '~@perpay-web/components/design-system/Box/Box.scss';
 *   --box-padding: #{$xs-space} #{$large-space};
 * }
 * ```
 */
const Box = forwardRef(({ as = 'div', className = '', children }, ref) => {
    const Tag = as;
    return (
        <Tag ref={ref} className={getClassName(styles.box, className)}>
            {children}
        </Tag>
    );
});

export default Box;
